/** What a page's map frames: the points it draws, the short way round the world. */
import type { MapPage } from './page';

/**
 * Every point the page's map draws in colour: its own ports, and its lines when it draws them; not the grey ports
 * (`kind: 'rest'`), which would frame the world for one long line, nor lines drawn only on hover (`hoverLines`).
 */
export function pageCoords(data: Pick<MapPage, 'routes' | 'ports' | 'hoverLines'>): [number, number][] {
  const coords: [number, number][] = [];
  if (!data.hoverLines) {
    for (const { geometry } of data.routes.features) {
      if (geometry.type === 'LineString') coords.push(...(geometry.coordinates as [number, number][]));
    }
  }
  for (const { geometry, properties } of data.ports.features) {
    if (geometry.type === 'Point' && properties?.kind !== 'rest') coords.push(geometry.coordinates as [number, number]);
  }
  return coords;
}

/**
 * The points on one side of the world: the frame leaves out the widest stretch of longitude with
 * nothing in it, so points either side of the dateline (Fiji at 178°, Samoa at -172°) are framed
 * across it, those west of the frame's start a turn east (MapLibre draws the world again past 180°),
 * not across the whole world the other way.
 */
export function shortWay(coords: [number, number][]): [number, number][] {
  const lngs = [...new Set(coords.map(([lng]) => lng))].sort((a, b) => a - b);
  if (lngs.length < 2) return coords;
  // The stretch across the dateline first: the frame starts at the westernmost point, nothing moved
  let gap = lngs[0] + 360 - lngs[lngs.length - 1];
  let start = lngs[0];
  for (let i = 1; i < lngs.length; i++) {
    if (lngs[i] - lngs[i - 1] > gap) [gap, start] = [lngs[i] - lngs[i - 1], lngs[i]];
  }
  return coords.map(([lng, lat]) => [lng < start ? lng + 360 : lng, lat]);
}
